import React, { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Link } from 'react-router-dom'
import useCanonicalUrl from '../hooks/useCanonicalUrl'
import useFAQSchema from '../hooks/useFAQSchema'
import ToolPageTemplate from '../templates/ToolPageTemplate'
import Input from '../atoms/Input'
import Button from '../atoms/Button'
import ResultBox from '../organisms/ResultBox'
import { setChronoBirthDate, setChronoTestDate, calculateChronological, clearChronological } from '../store/slices/ageSlice'
import '../pages/AgePage.css'

const ChronologicalAgeCalculatorPage = ({ tools = [] }) => {
    useCanonicalUrl()
    useFAQSchema()
    const dispatch = useDispatch()
    const { chronoBirthDate, chronoTestDate, chronoResult } = useSelector(state => state.age)
    const [lastUpdated, setLastUpdated] = useState(null)

    useEffect(() => {
        setLastUpdated(
            new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
        )
    }, [])

    useEffect(() => {
        document.title = 'Chronological Age Calculator - For Testing & Assessment | EveryKits'
        const metaDescription = document.querySelector('meta[name="description"]')
        if (metaDescription) {
            metaDescription.setAttribute('content', 'Calculate chronological age for psychological and educational testing (e.g. WISC, Pearson assessments) with accurate years;months format.')
        }
        const structuredData = {
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "Chronological Age Calculator",
            "applicationCategory": "WebApplication",
            "operatingSystem": "All",
            "offers": { "@type": "Offer", "price": "0", "priceCurrency": "USD" },
            "description": "Free chronological age calculator for testing and assessment purposes.",
            "url": "https://everykits.com/chronological-age-calculator/"
        }
        document.querySelectorAll('script[data-schema="software-application"]').forEach(el => el.remove());
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.setAttribute('data-schema', 'software-application');
        script.setAttribute('data-schema', 'software-application');
        script.textContent = JSON.stringify(structuredData)
        document.head.appendChild(script)
        return () => {
            if (document.head.contains(script)) {
                document.head.removeChild(script);
            }
        };
    }, [])

    return (
        <ToolPageTemplate
            title="Chronological Age Calculator"
            description="Calculate chronological age for testing and assessment purposes"
            tools={tools}
            breadcrumbItems={[
                { name: 'Home', path: '/' },
                { name: 'Age Calculator', path: '/age/' },
                { name: 'Chronological Age Calculator', path: '/chronological-age-calculator/' },
            ]}
        >
            <div>
                <section className="age-calculator">
                    <div className="age-input-section">
                        <Input
                            type="date"
                            label="Birth Date"
                            value={chronoBirthDate}
                            onChange={(e) => dispatch(setChronoBirthDate(e.target.value))}
                        />
                        <Input
                            type="date"
                            label="Test Date"
                            value={chronoTestDate}
                            onChange={(e) => dispatch(setChronoTestDate(e.target.value))}
                        />
                        <div className="age-buttons">
                            <Button onClick={() => dispatch(calculateChronological())} disabled={!chronoBirthDate || !chronoTestDate}>Calculate Chronological Age</Button>
                            <Button variant="secondary" onClick={() => dispatch(clearChronological())}>Clear</Button>
                        </div>
                    </div>

                    {chronoResult && (
                        <ResultBox title="Chronological Age (for testing/assessment use)">
                            <div className="age-details">
                                <div className="age-item">
                                    <span className="age-label">Years:</span>
                                    <span className="age-value">{chronoResult.years}</span>
                                </div>
                                <div className="age-item">
                                    <span className="age-label">Months:</span>
                                    <span className="age-value">{chronoResult.months}</span>
                                </div>
                                <div className="age-item">
                                    <span className="age-label">Format:</span>
                                    <span className="age-value">{chronoResult.formatted}</span>
                                </div>
                            </div>
                        </ResultBox>
                    )}
                </section>
                <section className="tool-guide">
                    <div className="guide-section">
                        <h2>How to Use Chronological Age Calculator</h2>
                        <div className="guide-steps">
                            <div className="step">
                                <div className="step-number">1</div>
                                <div className="step-content">
                                    <h3>Enter Birth Date</h3>
                                    <p>Select the individual's date of birth using the calendar input.</p>
                                </div>
                            </div>
                            <div className="step">
                                <div className="step-number">2</div>
                                <div className="step-content">
                                    <h3>Enter Test Date</h3>
                                    <p>Select the exact date the assessment or test was administered.</p>
                                </div>
                            </div>
                            <div className="step">
                                <div className="step-number">3</div>
                                <div className="step-content">
                                    <h3>Get Standardized Result</h3>
                                    <p>The calculator applies standard psychometric rounding rules and displays the result in years;months format used by test publishers.</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="guide-section">
                        <h2>Why Use This Tool</h2>
                        <p>Chronological age calculations for psychological and educational assessments (such as WISC, WPPSI, or Pearson-published tests) require precise, standardized rounding — a simple date subtraction isn't enough. This calculator follows the widely-used convention of rounding 16+ days up to the next month, matching the method expected by test administrators and examiners, saving time and eliminating manual calculation errors.</p>
                    </div>
                    <div className="guide-section">
                        <h2>Frequently Asked Questions</h2>
                        <div className="faq-list">
                            <div className="faq-item">
                                <h3>What is chronological age?</h3>
                                <p>Chronological age is the exact time elapsed between a person's birth date and a specific test date, used in psychological and educational assessments like WISC testing.</p>
                            </div>
                            <div className="faq-item">
                                <h3>Why does it round at 16 days?</h3>
                                <p>Standard psychometric convention (used by Pearson and similar test publishers) rounds 16+ days up to the next month for consistency in scoring.</p>
                            </div>
                            <div className="faq-item">
                                <h3>Is this different from regular age?</h3>
                                <p>Yes, chronological age uses a specific test date rather than today's date, and follows strict rounding rules required for standardized testing.</p>
                            </div>
                        </div>
                    </div>
                </section>
                <section className="tool-guide">
                    <div className="guide-section">
                        <h2>Related Tools</h2>
                        <div className="related-tools">
                            <Link to="/age/" className="related-tool">
                                <div className="tool-icon">Age Calculator</div>
                                <div className="tool-description">Calculate your exact age from date of birth</div>
                            </Link>
                            <Link to="/eligibility-calculator/" className="related-tool">
                                <div className="tool-icon">Eligibility Calculator</div>
                                <div className="tool-description">Check exam and milestone age eligibility</div>
                            </Link>
                        </div>
                    </div>
                </section>

                <div className="last-updated">
                    <p suppressHydrationWarning>Last updated: {lastUpdated || 'Recently'}</p>
                </div>
            </div>
        </ToolPageTemplate>
    )
}

export default ChronologicalAgeCalculatorPage